/**
 * PermissionSelector Component
 * Simple checkbox-based permission selection with tooltips for descriptions
 */

import { useState, useMemo } from 'react';
import { CheckIcon, SparklesIcon } from '@heroicons/react/24/outline';

// ============================================
// Permission Presets
// ============================================
const ADMIN_SLUGS = new Set([
  'CREATE:USERS', 'READ:USERS', 'UPDATE:USERS',
  'UPDATE:AGENCY:PROFILE', 'UPDATE:AGENCY:PAYMENT-ACCOUNT',
]);

const PERMISSION_PRESETS = [
  {
    name: 'Full Access',
    slugs: null, // Special: all available except admin
    variant: 'amber',
    requiresConfirm: true,
  },
  {
    name: 'Transfer Bonds Only',
    slugs: ['CREATE:TRANSFER-BOND:REQUEST', 'UPDATE:TRANSFER-BOND:POST', 'READ:PORTAL', 'READ:POWERS'],
  },
  {
    name: 'Large Bonds Only',
    slugs: ['CREATE:LARGE-BOND:REQUEST', 'READ:PORTAL', 'READ:POWERS'],
  },
  {
    name: 'Transfer & Large Bonds',
    slugs: ['CREATE:LARGE-BOND:REQUEST', 'CREATE:TRANSFER-BOND:REQUEST', 'UPDATE:TRANSFER-BOND:POST', 'READ:PORTAL', 'READ:POWERS'],
  },
  {
    name: 'Discharge & Reinstate Only',
    slugs: ['UPDATE:POWER:DISCHARGE-REINSTATE', 'READ:PORTAL', 'READ:POWERS'],
  },
];

// Tooltip component
const Tooltip = ({ children, content }) => {
  const [show, setShow] = useState(false);

  if (!content) return children;

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div className="absolute z-50 bottom-full left-0 mb-1.5 px-2 py-1 text-xs bg-gray-900 dark:bg-gray-700 text-white rounded shadow-lg whitespace-nowrap max-w-xs">
          {content}
          <div className="absolute top-full left-3 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
        </div>
      )}
    </div>
  );
};

// Friendly labels for permissions
const PERMISSION_LABELS = {
  // User Management
  'CREATE:USERS': 'Create User Accounts',
  'READ:USERS': 'Access User Accounts',
  'UPDATE:USERS': 'Update User Accounts',
  // Agency
  'READ:AGENCY-WORK': 'Access Agency Work',
  'UPDATE:AGENCY:PROFILE': 'Manage Agency Profile',
  'UPDATE:AGENCY:PAYMENT-ACCOUNT': 'Manage Agency Payment Accounts',
  'READ:AGENCY': 'Access Agency',
  'READ:SUB-AGENCIES': 'Access Sub-Agencies',
  // Powers
  'READ:POWERS': 'Access Powers',
  'READ:POWER-LOG': 'Access Power Log',
  'READ:POWER-REASSIGNMENT': 'Access Power Reassignment',
  'UPDATE:POWER:DISCHARGE-REINSTATE': 'Discharge/Reinstate Powers',
  'UPDATE:POWER:DISCHARGE:OVERRIDE': 'Discharge Override Limit',
  'UPDATE:POWER:DISCHARGE:OVERRIDE:FORFEITURE': 'Discharge Override (Forfeiture & Reinstatement)',
  // Transfer Bonds
  'CREATE:TRANSFER-BOND:REQUEST': 'Request Transfer Bonds',
  'UPDATE:TRANSFER-BOND:POST': 'Post Transfer Bonds',
  'READ:TRANSFER-BOND-POSTING-AGENCY': 'View Posting Agency Names',
  'READ:TRANSFER-BOND-REQUESTING-AGENCY': 'View Requesting Agency Names',
  // Large Bonds
  'CREATE:LARGE-BOND:REQUEST': 'Request Large Bond',
  // Financial
  'READ:EXECUTION-REPORT': 'Access Execution Report',
  'READ:AR': 'Access A/R',
  'READ:ELECTRONIC-PAYMENT': 'Electronic Payment',
  'READ:VARIABLE-RATE': 'Variable Rate',
  // Contract
  'READ:CONTRACT': 'Access Contract Information',
  // General / Portal
  'READ:PORTAL': 'Access Portal',
  'READ:DASHBOARD': 'Access Dashboard',
  'READ:REPORT-CENTER': 'Access Report Center',
  'READ:FORMS': 'Access To Forms',
  'READ:PROPERTY-SEARCH': 'Access Property Search',
};

// Descriptions for permission tooltips
const PERMISSION_DESCRIPTIONS = {
  // User Management
  'CREATE:USERS': 'Can create a user account.',
  'READ:USERS': 'Can see a user account.',
  'UPDATE:USERS': 'Can manage other user accounts. Edit information.',
  // Agency
  'READ:AGENCY-WORK': 'Can view bonds, powers, and transactions submitted by all users within the agency. Without this, users are limited to their own work.',
  'UPDATE:AGENCY:PROFILE': 'Can manage agency profile.',
  'UPDATE:AGENCY:PAYMENT-ACCOUNT': 'Can manage agency payment accounts.',
  'READ:AGENCY': 'Can access the agency.',
  'READ:SUB-AGENCIES': 'Can access sub-agencies (if applicable).',
  // Powers
  'READ:POWERS': 'Can access power search and power details.',
  'READ:POWER-LOG': 'Can access power log.',
  'READ:POWER-REASSIGNMENT': 'Can access power reassignment.',
  'UPDATE:POWER:DISCHARGE-REINSTATE': 'Can discharge and reinstate powers.',
  'UPDATE:POWER:DISCHARGE:OVERRIDE': 'Allows elevated discharge limits.',
  'UPDATE:POWER:DISCHARGE:OVERRIDE:FORFEITURE': 'Allows elevated forfeiture and reinstatement discharge limits.',
  // Transfer Bonds
  'CREATE:TRANSFER-BOND:REQUEST': 'Can request transfer bonds.',
  'UPDATE:TRANSFER-BOND:POST': 'Can post transfer bonds.',
  'READ:TRANSFER-BOND-POSTING-AGENCY': 'Can view posting agency names in transfer bonds.',
  'READ:TRANSFER-BOND-REQUESTING-AGENCY': 'Can view requesting agency names in transfer bonds.',
  // Large Bonds
  'CREATE:LARGE-BOND:REQUEST': 'Can request large bonds.',
  // Financial
  'READ:EXECUTION-REPORT': 'Can access execution reports. Submitting an execution report creates a balance due to the surety.',
  'READ:AR': 'Can view A/R, payment accounts, agency vital report card, agency statement, or any payment balances.',
  'READ:ELECTRONIC-PAYMENT': 'Can access electronic payments (ACH).',
  'READ:VARIABLE-RATE': 'Access to variable surety rates.',
  // Contract
  'READ:CONTRACT': 'Restricts visibility of UWA, contract details, and surety rate.',
  // General / Portal
  'READ:PORTAL': 'Can access the portal.',
  'READ:DASHBOARD': 'Can access the dashboard.',
  'READ:REPORT-CENTER': 'Can access the report center.',
  'READ:FORMS': 'Can download and order forms.',
  'READ:PROPERTY-SEARCH': 'Can access property search.',
};

const getDescription = (permissionName) => {
  return PERMISSION_DESCRIPTIONS[permissionName] || null;
};

const getLabel = (slug, perm) => {
  // Prefer API display name, fall back to our label map, then auto-format
  if (perm?.permissionName) return perm.permissionName;
  if (PERMISSION_LABELS[slug]) return PERMISSION_LABELS[slug];

  return slug
    .split(':')
    .map(part => part.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' '))
    .join(' ');
};

// Category color schemes for visual distinction
const CATEGORY_COLORS = [
  { border: 'border-l-blue-500', bg: 'bg-blue-50/50 dark:bg-blue-900/10', text: 'text-blue-700 dark:text-blue-300' },
  { border: 'border-l-emerald-500', bg: 'bg-emerald-50/50 dark:bg-emerald-900/10', text: 'text-emerald-700 dark:text-emerald-300' },
  { border: 'border-l-purple-500', bg: 'bg-purple-50/50 dark:bg-purple-900/10', text: 'text-purple-700 dark:text-purple-300' },
  { border: 'border-l-amber-500', bg: 'bg-amber-50/50 dark:bg-amber-900/10', text: 'text-amber-700 dark:text-amber-300' },
  { border: 'border-l-rose-500', bg: 'bg-rose-50/50 dark:bg-rose-900/10', text: 'text-rose-700 dark:text-rose-300' },
  { border: 'border-l-cyan-500', bg: 'bg-cyan-50/50 dark:bg-cyan-900/10', text: 'text-cyan-700 dark:text-cyan-300' },
];

// Simple checkbox (visual only - click handled by parent)
const Checkbox = ({ checked, disabled, inherited }) => (
  <div
    className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 pointer-events-none ${
      checked
        ? inherited ? 'bg-gray-400 dark:bg-gray-500 border-gray-400 dark:border-gray-500' : 'bg-primary border-primary'
        : 'border-gray-300 dark:border-gray-500'
    } ${disabled ? 'opacity-50' : ''}`}
  >
    {checked && <CheckIcon className="h-2.5 w-2.5 text-white stroke-[3]" />}
  </div>
);

const PermissionSelector = ({
  availablePermissions = [],
  selectedPermissions = [],
  inheritedPermissionIds = [],
  onChange,
  loading = false,
  error = null,
  disabled = false,
}) => {
  const permissionsByCategory = useMemo(() => {
    return availablePermissions.reduce((acc, perm) => {
      const category = perm.category || 'Other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(perm);
      return acc;
    }, {});
  }, [availablePermissions]);

  // Build a slug → id lookup from available permissions
  // API returns slug as `permissionKey`, display name as `permissionName`
  const getSlug = (p) => p.permissionKey || '';

  const slugToId = useMemo(() => {
    const map = {};
    availablePermissions.forEach(p => { map[getSlug(p)] = p.portalPermissionId; });
    return map;
  }, [availablePermissions]);

  const allAvailableSlugs = useMemo(() => new Set(availablePermissions.map(p => getSlug(p))), [availablePermissions]);

  // Resolve presets: compute ids, check visibility, detect active state
  const [showConfirmPreset, setShowConfirmPreset] = useState(null);

  const resolvedPresets = useMemo(() => {
    return PERMISSION_PRESETS.map(preset => {
      let presetIds;
      if (preset.slugs === null) {
        // Full Access: all available except admin slugs
        presetIds = availablePermissions
          .filter(p => !ADMIN_SLUGS.has(getSlug(p)))
          .map(p => p.portalPermissionId);
      } else {
        // Check all slugs are available
        const allSlugsAvailable = preset.slugs.every(s => allAvailableSlugs.has(s));
        if (!allSlugsAvailable) return null; // Hide preset
        presetIds = preset.slugs.map(s => slugToId[s]).filter(Boolean);
      }

      // Check if currently active: preset ids match selected (excluding inherited)
      const nonInheritedSelected = selectedPermissions.filter(id => !inheritedPermissionIds.includes(id));
      const presetIdSet = new Set(presetIds);
      const isActive = nonInheritedSelected.length === presetIds.length &&
        nonInheritedSelected.every(id => presetIdSet.has(id));

      return { ...preset, ids: presetIds, isActive };
    }).filter(Boolean);
  }, [availablePermissions, allAvailableSlugs, slugToId, selectedPermissions, inheritedPermissionIds]);

  const applyPreset = (preset) => {
    if (preset.requiresConfirm) {
      setShowConfirmPreset(preset);
      return;
    }
    const inherited = selectedPermissions.filter(id => inheritedPermissionIds.includes(id));
    onChange([...new Set([...inherited, ...preset.ids])]);
  };

  const confirmPreset = () => {
    if (showConfirmPreset) {
      const inherited = selectedPermissions.filter(id => inheritedPermissionIds.includes(id));
      onChange([...new Set([...inherited, ...showConfirmPreset.ids])]);
      setShowConfirmPreset(null);
    }
  };

  const togglePermission = (permissionId) => {
    if (disabled || inheritedPermissionIds.includes(permissionId)) return;
    const newSelected = selectedPermissions.includes(permissionId)
      ? selectedPermissions.filter(id => id !== permissionId)
      : [...selectedPermissions, permissionId];
    onChange(newSelected);
  };

  const toggleCategory = (categoryPerms) => {
    if (disabled) return;
    // Exclude inherited permissions from bulk toggle
    const toggleableIds = categoryPerms
      .map(p => p.portalPermissionId)
      .filter(id => !inheritedPermissionIds.includes(id));
    if (toggleableIds.length === 0) return;

    const allToggleableSelected = toggleableIds.every(id => selectedPermissions.includes(id));

    if (allToggleableSelected) {
      // Deselect all toggleable, keep inherited
      onChange(selectedPermissions.filter(id => !toggleableIds.includes(id)));
    } else {
      onChange([...new Set([...selectedPermissions, ...toggleableIds])]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-600 dark:text-red-400 py-4">
        {error}
      </div>
    );
  }

  if (availablePermissions.length === 0) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 py-4">
        No permissions available
      </div>
    );
  }

  const categories = Object.entries(permissionsByCategory);

  return (
    <div className={`space-y-4 ${disabled ? 'opacity-60' : ''}`}>
      {/* Preset bar */}
      {!disabled && resolvedPresets.length > 0 && (
        <div className="flex flex-col gap-2 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-1.5">
            <SparklesIcon className="h-3.5 w-3.5 text-primary dark:text-primary-light" />
            <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Quick Presets</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {resolvedPresets.map((preset) => (
              <button
                key={preset.name}
                type="button"
                onClick={() => applyPreset(preset)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 ${
                  preset.isActive
                    ? 'bg-primary text-white shadow-sm'
                    : preset.variant === 'amber'
                      ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/30'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-primary/10 dark:hover:bg-primary/20 hover:text-primary dark:hover:text-primary-light hover:border-primary/30 dark:hover:border-primary/40'
                }`}
              >
                {preset.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Full Access confirmation dialog */}
      {showConfirmPreset && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg animate-expand-down">
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Apply "{showConfirmPreset.name}"?</p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">This will grant all permissions except admin-level access.</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowConfirmPreset(null)}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmPreset}
              className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {categories.map(([category, perms], index) => {
        const ids = perms.map(p => p.portalPermissionId);
        const selectedCount = ids.filter(id => selectedPermissions.includes(id)).length;
        const allSelected = selectedCount === perms.length;
        const someSelected = selectedCount > 0 && !allSelected;
        const colors = CATEGORY_COLORS[index % CATEGORY_COLORS.length];

        return (
          <div
            key={category}
            className={`rounded-xl border-l-4 ${colors.border} ${colors.bg} p-4`}
          >
            {/* Category header */}
            <div
              role="button"
              tabIndex={disabled ? -1 : 0}
              onClick={() => !disabled && toggleCategory(perms)}
              onKeyDown={(e) => {
                if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  toggleCategory(perms);
                }
              }}
              className={`flex items-center gap-2 mb-3 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div
                className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 pointer-events-none ${
                  allSelected
                    ? 'bg-primary border-primary'
                    : someSelected
                    ? 'bg-primary/50 border-primary'
                    : 'border-gray-300 dark:border-gray-500'
                } ${disabled ? 'opacity-50' : ''}`}
              >
                {(allSelected || someSelected) && (
                  <CheckIcon className="h-2.5 w-2.5 text-white stroke-[3]" />
                )}
              </div>
              <span className={`text-sm font-semibold ${colors.text}`}>
                {category}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                ({selectedCount}/{perms.length})
              </span>
            </div>

            {/* Permissions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-2 ml-6">
              {perms.map((perm) => {
                const isSelected = selectedPermissions.includes(perm.portalPermissionId);
                const isInherited = inheritedPermissionIds.includes(perm.portalPermissionId);
                const isDisabled = disabled || isInherited;
                const description = isInherited
                  ? 'Inherited from General Agency'
                  : getDescription(getSlug(perm));
                return (
                  <div
                    key={perm.portalPermissionId}
                    role="button"
                    tabIndex={isDisabled ? -1 : 0}
                    onClick={() => !isDisabled && togglePermission(perm.portalPermissionId)}
                    onKeyDown={(e) => {
                      if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        togglePermission(perm.portalPermissionId);
                      }
                    }}
                    className={`flex items-center gap-2 py-1 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <Checkbox checked={isSelected} disabled={isDisabled} inherited={isInherited} />
                    <Tooltip content={description}>
                      <span className={`text-sm ${
                        isInherited
                          ? 'text-gray-400 dark:text-gray-500 italic'
                          : isSelected
                          ? 'text-gray-900 dark:text-white'
                          : 'text-gray-600 dark:text-gray-400'
                      } ${description ? 'border-b border-dotted border-gray-400 dark:border-gray-500' : ''}`}>
                        {getLabel(getSlug(perm), perm)}
                      </span>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PermissionSelector;
